import type { ReactNode } from 'react';
import type { FetchState } from '../lib/useFetch.js';

export function Loading({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="state" role="status" aria-live="polite">
      <span className="spinner" /> {label}
    </div>
  );
}

export function ErrorState({ message }: { message: string }) {
  return (
    <div className="state error" role="alert">
      ⚠ {message}
    </div>
  );
}

export function Empty({ message = 'Nothing here yet.' }: { message?: string }) {
  return <div className="state">{message}</div>;
}

/**
 * Renders loading / error / empty / content from a FetchState — the uniform
 * loading-empty-error handling every tab uses.
 */
export function Async<T>({
  state,
  emptyWhen,
  emptyMessage,
  children,
}: {
  state: FetchState<T>;
  emptyWhen?: (data: T) => boolean;
  emptyMessage?: string;
  children: (data: T) => ReactNode;
}) {
  if (state.loading) return <Loading />;
  if (state.error) return <ErrorState message={state.error} />;
  if (state.data == null) return <Empty message={emptyMessage} />;
  if (emptyWhen && emptyWhen(state.data)) return <Empty message={emptyMessage} />;
  return <>{children(state.data)}</>;
}
